<template>
  <tr
    class="bg-orange-200/30 text-sm child:py-3 first-child:pl-3 last-child:pr-3 child:border-b child:border-gray-200 align-middle"
    :data-scope-gap-item-id="scopeGapItem.id"
    :data-monthly-scope-gap-forecast-id="currentForecast.id"
  >
    <td class="text-left">
      <div class="flex items-center gap-2">
        <button
          class="scope-gap-item-drag-handle cursor-grab active:cursor-grabbing p-1 rounded-md hover:bg-gray-200 transition-colors shrink-0"
          title="Drag to reorder item"
          type="button"
          @click.stop
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
        </button>

        <div
          v-if="canEdit"
          class="flex items-center justify-end gap-1 shrink-0"
        >
          <template v-if="!editingRow">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              title="Move to category"
              @click="openCategorySelect"
            >
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
    />
</svg>
            </button>

            <button
              type="button"
              class="btn-icon btn-icon-red"
              title="Remove row"
              @click="deleteForecast"
            >
              <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
            </button>

            <button
              type="button"
              class="btn-icon btn-icon-blue"
              title="Edit row"
              @click="startEditingRow"
            >
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
            </button>
          </template>

          <template v-else>
            <button
              type="button"
              class="btn-icon btn-icon-red"
              title="Cancel"
              @click="cancelRow"
            >
              <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
            </button>

            <button
              type="button"
              class="btn-icon-green btn-icon"
              title="Save row"
              @click="saveRow"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
            </button>
          </template>
        </div>

        <div class="min-w-0 flex-1">
          <AppInput
            v-if="canEdit && editingRow"
            ref="descriptionInput"
            v-model="description"
            name="description"
            type="text"
            class="w-full form-input h-8"
          />

          <span v-else>
            {{ scopeGapItem.description }}
          </span>
        </div>
      </div>
    </td>

    <td class="text-right pr-3">
      <AppCurrencyInput
        v-if="canEdit && (editingRow || editingAmount)"
        ref="amountInput"
        v-model.number="finalAmount"
        name="final_amount"
        type="number"
        class="w-28 h-8 ml-auto form-input"
        @blur="!editingRow && saveAmount()"
        @keyup.enter="$event.target.blur()"
        @keyup.esc="!editingRow && cancelAmount()"
      />

      <div
        v-else
        class="group flex items-center justify-end"
        :class="{ 'cursor-pointer': canEdit }"
        @click="startEditingAmount"
      >
        <button
          v-if="canEdit"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
          title="Edit amount"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <span>
          {{ australianCurrency(scopeGapItem.final_amount) }}
        </span>
      </div>
    </td>

    <td />

    <td />

    <td />

    <td />

    <td v-if="showPercentages" />

    <td v-if="showPercentages" />

    <td v-if="showPercentages" />

    <td v-if="showForecasts" />

    <td v-if="ratesColumn" />

    <td />

    <td />

    <td class="text-right pr-3">
      <AppInput
        v-if="canEdit && (editingRow || editingForecastPercent)"
        ref="forecastPercentInput"
        v-model.number="forecastPercent"
        name="forecast_percent"
        type="number"
        min="0"
        max="100"
        step="0.01"
        class="w-24 h-8 ml-auto form-input"
        @focus="percentFocused = true"
        @blur="percentFocused = false; !editingRow && saveForecastPercent()"
        @keyup.enter="$event.target.blur()"
        @keyup.esc="!editingRow && cancelForecastPercent()"
      />

      <div
        v-else
        class="group flex items-center justify-end"
        :class="{ 'cursor-pointer': canEdit }"
        @click="startEditingForecastPercent"
      >
        <button
          v-if="canEdit"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
          title="Edit forecast percent"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <span>
          {{ $tp(forecastPercent) }}
        </span>
      </div>
    </td>

    <td class="text-right pr-3">
      <AppCurrencyInput
        v-if="canEdit && (editingRow || editingCurrentForecast)"
        ref="currentForecastInput"
        v-model.number="currentForecastAmount"
        name="forecast_amount"
        type="number"
        class="w-28 h-8 ml-auto form-input"
        @focus="amountFocused = true"
        @blur="amountFocused = false; !editingRow && saveCurrentForecast()"
        @keyup.enter="$event.target.blur()"
        @keyup.esc="!editingRow && cancelCurrentForecast()"
      />

      <div
        v-else
        class="group flex items-center justify-end"
        :class="{ 'cursor-pointer': canEdit && currentForecast }"
        @click="startEditingCurrentForecast"
      >
        <button
          v-if="canEdit && currentForecast"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
          title="Edit current forecast"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <div>
          <p>
            {{ australianCurrency(currentForecastAmount) }}
          </p>

          <p
            class="text-xs"
            :class="varianceClass"
          >
            {{ australianCurrency(variance) }} m-1
          </p>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, roundDecimals } from '@/helpers';
import MonthlyScopeGapForecast from '@/models/MonthlyScopeGapForecast';
import ScopeGapItem from '@/models/ScopeGapItem';
import { useMonthlyScopeGapForecastsStore } from '@/store/monthlyScopeGapForecasts';
import { useScopeGapCategoriesStore } from '@/store/scopeGapCategories';
import { useScopeGapItemsStore } from '@/store/scopeGapItems';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    scopeGapItem: ScopeGapItem;
    currentForecast: MonthlyScopeGapForecast;
    previousForecast?: MonthlyScopeGapForecast | undefined;
    canEdit: boolean;
    showDragHandle?: boolean;
    showPercentages?: boolean;
    showForecasts?: boolean;
    ratesColumn?: boolean;
  }>(),
  {
    previousForecast: undefined,
    showDragHandle: false,
    showPercentages: false,
    showForecasts: false,
    ratesColumn: false,
  },
);

const emits = defineEmits<{
  (e: 'refresh');
}>();

const scopeGapItemsStore = useScopeGapItemsStore();
const scopeGapCategoriesStore = useScopeGapCategoriesStore();
const monthlyScopeGapForecastsStore = useMonthlyScopeGapForecastsStore();
const updateScopeGapItemAction = useStoreApiAction(scopeGapItemsStore.update);
const updateMonthlyForecastAction = useStoreApiAction(monthlyScopeGapForecastsStore.update);
const destroyMonthlyForecastAction = useStoreApiAction(monthlyScopeGapForecastsStore.destroy);
const description = ref(props.scopeGapItem.description);
const finalAmount = ref(props.scopeGapItem.final_amount);
const forecastPercent = ref(props.scopeGapItem.forecastPercent(props.currentForecast.forecast_amount));
const currentForecastAmount = ref(props.currentForecast.forecast_amount);
const editingRow = ref(false);
const editingAmount = ref(false);
const editingForecastPercent = ref(false);
const editingCurrentForecast = ref(false);
const amountFocused = ref(false);
const percentFocused = ref(false);
const descriptionInput = useTemplateRef('descriptionInput');
const amountInput = useTemplateRef('amountInput');
const forecastPercentInput = useTemplateRef('forecastPercentInput');
const currentForecastInput = useTemplateRef('currentForecastInput');
const oldForecastAmount = ref(props.currentForecast.forecast_amount);

const categories = computed(() => {
  return scopeGapCategoriesStore.models.orderBy('sort_order').get();
});

const categorySwalInputOptions = computed(() => {
  return {
    '': 'Uncategorized',
    ...Object.fromEntries(
      categories.value.map((category) => [String(category.id), category.description]),
    ),
  };
});

watch(forecastPercent, (newValue) => {
  if(editingForecastPercent.value || editingRow.value) {
    const baseAmount = editingRow.value ? (finalAmount.value ?? 0) : props.scopeGapItem.final_amount;

    currentForecastAmount.value = newValue ? roundDecimals(baseAmount * (newValue / 100)) : 0;
  }
});

watch(finalAmount, (newValue) => {
  if(editingRow.value && !percentFocused.value && !amountFocused.value && newValue) {
    forecastPercent.value = currentForecastAmount.value ?
      roundDecimals((currentForecastAmount.value / newValue) * 100) :
      0;
  }
});

watch(currentForecastAmount, (newValue) => {
  if((editingCurrentForecast.value || editingRow.value) && amountFocused.value && !percentFocused.value) {
    const baseAmount = editingRow.value ? (finalAmount.value ?? 0) : props.scopeGapItem.final_amount;

    forecastPercent.value = newValue && baseAmount ?
      roundDecimals((newValue / baseAmount) * 100) :
      0;
  }
});

const variance = computed(() => {
  if(!props.currentForecast) return undefined;

  const previousAmount = props.previousForecast?.forecast_amount ?? 0;

  return props.currentForecast.forecast_amount - previousAmount;
});

const varianceClass = computed(() => {
  if(variance.value === undefined || variance.value === 0) return 'text-gray-900';

  return variance.value > 0 ? 'text-red-600' : 'text-green-600';
});

function refresh() {
  emits('refresh');
}

function startEditingRow() {
  if(!props.canEdit || editingAmount.value || editingForecastPercent.value || editingCurrentForecast.value) return;

  description.value = props.scopeGapItem.description;
  finalAmount.value = props.scopeGapItem.final_amount ?? undefined;
  forecastPercent.value = props.scopeGapItem.forecastPercent(props.currentForecast.forecast_amount);
  currentForecastAmount.value = props.currentForecast.forecast_amount;
  editingRow.value = true;

  nextTick(() => descriptionInput.value?.focusInput());
}

function cancelRow() {
  description.value = props.scopeGapItem.description;
  finalAmount.value = props.scopeGapItem.final_amount ?? undefined;
  forecastPercent.value = props.scopeGapItem.forecastPercent(props.currentForecast.forecast_amount);
  currentForecastAmount.value = props.currentForecast.forecast_amount;
  editingRow.value = false;
}

async function saveRow() {
  const itemUpdates: Record<string, unknown> = {};

  if(description.value !== props.scopeGapItem.description) {
    itemUpdates.description = description.value;
  }

  if(finalAmount.value !== props.scopeGapItem.final_amount) {
    itemUpdates.final_amount = finalAmount.value;
  }

  const forecastChanged = props.currentForecast &&
    currentForecastAmount.value !== undefined &&
    currentForecastAmount.value !== props.currentForecast.forecast_amount;

  if(Object.keys(itemUpdates).length === 0 && !forecastChanged) {
    editingRow.value = false;

    return;
  }

  if(Object.keys(itemUpdates).length > 0) {
    await updateScopeGapItemAction.request(props.scopeGapItem.id, itemUpdates);
  }

  if(forecastChanged) {
    await updateMonthlyForecastAction.request(props.currentForecast.id, {
      forecast_amount: currentForecastAmount.value,
    });
  }

  editingRow.value = false;
  refresh();
}

function startEditingAmount() {
  if(!props.canEdit || editingRow.value) return;

  finalAmount.value = props.scopeGapItem.final_amount ?? undefined;
  editingAmount.value = true;

  nextTick(() => amountInput.value?.focusInput());
}

function cancelAmount() {
  finalAmount.value = props.scopeGapItem.final_amount ?? undefined;
  editingAmount.value = false;
}

async function saveAmount() {
  editingAmount.value = false;

  if(finalAmount.value === props.scopeGapItem.final_amount) return;

  await updateScopeGapItemAction.request(props.scopeGapItem.id, {
    final_amount: finalAmount.value,
  });

  refresh();
}

function startEditingForecastPercent() {
  if(!props.canEdit || editingRow.value) return;

  finalAmount.value = props.scopeGapItem.final_amount ?? undefined;
  currentForecastAmount.value = props.currentForecast?.forecast_amount;
  editingForecastPercent.value = true;

  nextTick(() => forecastPercentInput.value?.focusInput());
}

function cancelForecastPercent() {
  forecastPercent.value = undefined;
  editingForecastPercent.value = false;
}

async function saveForecastPercent() {
  editingForecastPercent.value = false;

  if(
    !props.currentForecast || currentForecastAmount.value === undefined ||
    currentForecastAmount.value === props.currentForecast.forecast_amount
  ) return;

  await updateMonthlyForecastAction.request(props.currentForecast.id, {
    forecast_amount: currentForecastAmount.value,
  });

  refresh();
}

function startEditingCurrentForecast() {
  if(!props.canEdit || editingRow.value || !props.currentForecast) return;

  currentForecastAmount.value = props.currentForecast.forecast_amount;
  editingCurrentForecast.value = true;

  nextTick(() => currentForecastInput.value?.focusInput());
}

function cancelCurrentForecast() {
  editingCurrentForecast.value = false;
}

async function saveCurrentForecast() {
  editingCurrentForecast.value = false;

  if(
    !props.currentForecast || currentForecastAmount.value === undefined ||
    currentForecastAmount.value === props.currentForecast.forecast_amount
  ) return;

  await updateMonthlyForecastAction.request(props.currentForecast.id, {
    forecast_amount: currentForecastAmount.value,
  });

  refresh();
}

function openCategorySelect() {
  if(!props.canEdit || !categories.value.length) return;

  const currentCategoryId = props.scopeGapItem.scope_gap_category_id;

  Swal.fire({
    title: 'Move to category',
    input: 'select',
    inputOptions: categorySwalInputOptions.value,
    inputValue: currentCategoryId !== undefined ? String(currentCategoryId) : '',
    inputPlaceholder: 'Select a category',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      assignCategory(result.value);
    }
  });
}

async function assignCategory(categoryId: number | string | undefined) {
  const normalizedCategoryId = categoryId === '' || categoryId === undefined ?
    undefined :
    Number(categoryId);

  if(normalizedCategoryId === props.scopeGapItem.scope_gap_category_id) return;

  await updateScopeGapItemAction.request(props.scopeGapItem.id, {
    scope_gap_category_id: normalizedCategoryId,
  });

  refresh();
}

function deleteForecast() {
  if(!props.canEdit || !props.currentForecast) return;

  Swal.fire({
    icon: 'warning',
    title: 'Remove scope gap item?',
    text: 'Are you sure you want to remove this scope gap item?',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      destroyMonthlyForecastAction.request(props.currentForecast.id);
    }
  });
}
</script>
